/**
 * Mask Bundle Modal - Reusable Component
 * Handles modal open/close and bundle add to cart functionality
 * Works with any CPAP machine product that has the _machine_compatible_masks metafield
 */

if (!customElements.get('mask-bundle-modal')) {
  customElements.define(
    'mask-bundle-modal',
    class MaskBundleModal extends HTMLElement {
      constructor() {
        super();

        this.machineId = this.dataset.productId;
        this.machineVariantId = this.dataset.variantId;

        // Bind methods
        this.open = this.open.bind(this);
        this.close = this.close.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);
      }

      connectedCallback() {
        // Use setTimeout to ensure DOM is fully ready
        setTimeout(() => {
          // Find trigger button with better error handling
          const trigger = document.querySelector(`[data-open-bundle-modal][data-product-id="${this.machineId}"]`);
          if (trigger) {
            // Remove any existing listener first
            trigger.removeEventListener('click', this.open);
            trigger.addEventListener('click', this.open);
          } else {
            console.warn('Modal trigger button not found for product:', this.machineId);
          }

          // Setup close buttons
          const closeButtons = this.querySelectorAll('[data-modal-close]');
          closeButtons.forEach(btn => {
            btn.addEventListener('click', this.close);
          });

          // Listen for successful mask selection
          const forms = this.querySelectorAll('form');
          forms.forEach(form => {
            form.addEventListener('submit', this.handleBundleSubmit.bind(this));
          });
        }, 100);
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.handleKeyDown);
      }

      open() {
        this.classList.remove('tw-hidden');
        document.body.style.overflow = 'hidden';
        document.body.classList.add('modal-open');

        // Accessibility
        this.setAttribute('aria-hidden', 'false');
        this.querySelector('.modal-content')?.focus();

        // Listen for ESC key
        document.addEventListener('keydown', this.handleKeyDown);

        // Trap focus within modal
        this.trapFocus();

        // Initialize brand filters and disable variant fetching
        setTimeout(() => {
          if (typeof updateBrandFilters === 'function') {
            updateBrandFilters();
          }
          if (typeof disableModalVariantFetching === 'function') {
            disableModalVariantFetching();
          }
        }, 150);
      }

      close() {
        this.classList.add('tw-hidden');
        document.body.style.overflow = '';
        document.body.classList.remove('modal-open');

        // Accessibility
        this.setAttribute('aria-hidden', 'true');

        // Remove ESC listener
        document.removeEventListener('keydown', this.handleKeyDown);

        // Return focus to trigger button
        const trigger = document.querySelector(`[data-open-bundle-modal][data-product-id="${this.machineId}"]`);
        trigger?.focus();
      }

      handleKeyDown(e) {
        if (e.key === 'Escape') {
          this.close();
        }
      }

      handleBackdropClick(e) {
        if (e.target.hasAttribute('data-modal-close')) {
          this.close();
        }
      }

      trapFocus() {
        const focusableElements = this.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        this.addEventListener('keydown', (e) => {
          if (e.key !== 'Tab') return;

          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              lastElement.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastElement) {
              firstElement.focus();
              e.preventDefault();
            }
          }
        });
      }

      async handleBundleSubmit(evt) {
        evt.preventDefault();

        const form = evt.target.closest('form');
        if (!form) return;

        const submitButton = form.querySelector('[type="submit"]');
        const selectedMaskInput = this.querySelector('input[name="mask-product"]:checked');

        if (!selectedMaskInput) {
          this.showError('Please select a mask');
          return;
        }

        const maskVariantId = this.getCurrentMaskVariantId();

        if (!maskVariantId) {
          this.showError('Please select a mask variant/size');
          return;
        }

        // Disable button and show loading
        submitButton.setAttribute('aria-disabled', 'true');
        submitButton.classList.add('loading');
        submitButton.disabled = true;

        try {
          // Step 1: Add machine to cart
          await this.addToCart(this.machineVariantId, 1);

          // Step 2: Add selected mask to cart
          await this.addToCart(maskVariantId, 1);

          // Success: Close modal
          this.close();

          // Update cart count and open cart
          await this.updateCartAndOpen();

        } catch (error) {
          console.error('Bundle add to cart error:', error);
          this.showError(error.message || 'Error adding products to cart. Please try again.');

        } finally {
          submitButton.setAttribute('aria-disabled', 'false');
          submitButton.classList.remove('loading');
          submitButton.disabled = false;
        }
      }

      async addToCart(variantId, quantity = 1) {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);

        const config = {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          },
          body: formData
        };

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', config);

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || error.message || 'Failed to add to cart');
        }

        return response.json();
      }

      getCurrentMaskVariantId() {
        // Get selected mask radio button
        const selectedMask = this.querySelector('input[name="mask-product"]:checked');
        if (!selectedMask) {
          console.warn('No mask selected');
          return null;
        }

        const maskProductId = selectedMask.value;

        // Find the mask's product card
        const maskCard = selectedMask.closest('.mask__item');
        if (!maskCard) {
          console.warn('No mask card found');
          return null;
        }

        // Try to find variant-radios custom element and its current variant
        const variantRadios = maskCard.querySelector('variant-radios');
        if (variantRadios) {
          // Check if variant-radios has currentVariant property
          if (variantRadios.currentVariant && variantRadios.currentVariant.id) {
            return variantRadios.currentVariant.id;
          }

          // Look for hidden input managed by variant-radios
          const hiddenInput = variantRadios.querySelector('input[name="id"][type="hidden"]');
          if (hiddenInput && hiddenInput.value) {
            return hiddenInput.value;
          }
        }

        // Try to find variant select dropdown
        const variantSelect = maskCard.querySelector('select[name="id"]');
        if (variantSelect) {
          return variantSelect.value;
        }

        // Fallback: find any hidden input with name="id"
        const hiddenInput = maskCard.querySelector('input[name="id"]');
        if (hiddenInput) {
          return hiddenInput.value;
        }

        // Last resort: check data attribute on product-info
        const productInfo = maskCard.querySelector('product-info');
        if (productInfo && productInfo.dataset.variantId) {
          return productInfo.dataset.variantId;
        }

        console.warn('Could not find variant ID for selected mask');
        return null;
      }

      async updateCartAndOpen() {
        try {
          // Fetch updated cart JSON
          const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
          const cart = await cartResponse.json();

          console.log('Cart updated, item count:', cart.item_count);

          // Update cart count badges (try multiple selectors)
          const cartCountSelectors = [
            '.cart-count-bubble',
            '#cart-icon-bubble',
            '[data-cart-count]',
            '.cart-count'
          ];

          cartCountSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
              element.textContent = cart.item_count;
              if (element.hasAttribute('data-cart-count')) {
                element.setAttribute('data-cart-count', cart.item_count);
              }
            });
          });

          // Fetch and update cart drawer HTML
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) {
            try {
              // Fetch the cart section HTML
              const sectionResponse = await fetch(window.Shopify.routes.root + 'cart?section_id=cart-drawer');
              const sectionHTML = await sectionResponse.text();

              // Parse the HTML
              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionHTML, 'text/html');
              const newCartDrawerContent = doc.querySelector('cart-drawer');

              if (newCartDrawerContent) {
                // Update the cart drawer's innerHTML
                cartDrawer.innerHTML = newCartDrawerContent.innerHTML;
                console.log('Cart drawer content updated');
              }
            } catch (error) {
              console.warn('Could not update cart drawer HTML:', error);
            }

            // Open the drawer
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              cartDrawer.classList.add('active');
            }
            return;
          }

          // Fallback: Try cart notification
          const cartNotification = document.querySelector('cart-notification');
          if (cartNotification) {
            try {
              const sectionResponse = await fetch(window.Shopify.routes.root + 'cart?section_id=cart-notification');
              const sectionHTML = await sectionResponse.text();

              const parser = new DOMParser();
              const doc = parser.parseFromString(sectionHTML, 'text/html');
              const newContent = doc.querySelector('cart-notification');

              if (newContent) {
                cartNotification.innerHTML = newContent.innerHTML;
              }
            } catch (error) {
              console.warn('Could not update cart notification:', error);
            }

            if (typeof cartNotification.open === 'function') {
              cartNotification.open();
            }
            return;
          }

          // Last resort: Redirect to cart page
          console.log('No cart drawer found, redirecting to cart page');
          window.location.href = window.Shopify.routes.root + 'cart';

        } catch (error) {
          console.error('Error updating cart:', error);
          // On error, just redirect to cart page
          window.location.href = window.Shopify.routes.root + 'cart';
        }
      }

      showError(message) {
        // Try to find error message container
        const errorContainer = this.querySelector('.product-form__error-message-wrapper');
        if (errorContainer) {
          const errorMessage = errorContainer.querySelector('.product-form__error-message');
          if (errorMessage) {
            errorMessage.textContent = message;
            errorContainer.removeAttribute('hidden');

            // Hide after 5 seconds
            setTimeout(() => {
              errorContainer.setAttribute('hidden', '');
            }, 5000);
            return;
          }
        }

        // Fallback: use alert
        alert(message);
      }
    }
  );
}

// Update machine-mask-form to work within modal
if (!customElements.get('machine-mask-form')) {
  customElements.define(
    'machine-mask-form',
    class MachineMarkForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');

        if (this.form) {
          this.form.addEventListener('submit', (evt) => {
            // Let the parent modal handle submission via event bubbling
          });
        }
      }
    }
  );
}

// Global fallback: Event delegation for modal trigger buttons
// This ensures the button works even if custom elements have issues
document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-open-bundle-modal]');
    if (trigger) {
      e.preventDefault();
      const productId = trigger.dataset.productId;
      const modal = document.getElementById(`mask-bundle-modal-${productId}`);

      if (modal) {
        console.log('Opening modal via fallback for product:', productId);
        modal.classList.remove('tw-hidden');
        document.body.style.overflow = 'hidden';
        document.body.classList.add('modal-open');
        modal.setAttribute('aria-hidden', 'false');
      } else {
        console.error('Modal not found for product:', productId);
      }
    }

    // Close modal on backdrop or close button click
    const closeBtn = e.target.closest('[data-modal-close]');
    if (closeBtn) {
      const modal = closeBtn.closest('mask-bundle-modal');
      if (modal) {
        modal.classList.add('tw-hidden');
        document.body.style.overflow = '';
        document.body.classList.remove('modal-open');
        modal.setAttribute('aria-hidden', 'true');
      }
    }
  });
});
